import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import HoldCountdown from "@/components/HoldCountdown/HoldCountdown";
import PaymentForm from "@/components/PaymentForm/PaymentForm";
import PhotoPlaceholder from "@/components/PhotoPlaceholder/PhotoPlaceholder";
import stripeAppearance from "@/config/stripeAppearance";
import stripePromise from "@/config/stripeConfig";
import ApiError from "@/model/ApiError";
import BookingResponse from "@/model/BookingResponse";
import isoDate from "@/utils/isoDate";
import { formatMoney } from "@/utils/pricing";
import guestsUtils from "@/utils/guests";
import "@/pages/PaymentPage/PaymentPage.scss";

const STRIPE_FONTS_CSS =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap";

/** Pays a PENDING_PAYMENT booking with Stripe's Payment Element; also used to resume an unpaid checkout. */
function PaymentPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<"notAwaiting" | "generic" | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!session || !id) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    bookingsApi
      .getCheckout(session.accessToken, id)
      .then((checkout) => {
        if (!cancelled) {
          setBooking(checkout.booking ?? null);
          setClientSecret(checkout.clientSecret ?? null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError && err.status === 409
              ? "notAwaiting"
              : "generic"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  const handleExpired = useCallback(() => setIsExpired(true), []);

  if (!session || !id) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="payment-page">
        <p>{t("payment.loading")}</p>
      </div>
    );
  }

  if (error || !booking || !clientSecret) {
    return (
      <div className="payment-page">
        <div className="payment-page__message" role="alert">
          <p>
            {error === "notAwaiting"
              ? t("payment.notAwaiting")
              : t("payment.loadError")}
          </p>
          <Link to="/trips">{t("payment.toTrips")}</Link>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="payment-page">
        <p className="payment-page__message" role="alert">
          {t("payment.notConfigured")}
        </p>
      </div>
    );
  }

  const total = formatMoney(
    booking.totalAmount,
    booking.currency,
    i18n.language
  );

  // Booking requests only authorize the card; it is charged when the host accepts.
  const isRequest = booking.bookingMode === "REQUEST";

  return (
    <div className="payment-page">
      <p className="eyebrow">{t("payment.eyebrow")}</p>
      <h1>{isRequest ? t("payment.requestTitle") : t("payment.title")}</h1>
      {isRequest && <p className="payment-page__request-note">{t("payment.requestNote")}</p>}

      <div className="payment-page__layout">
        <section className="payment-page__form">
          {booking.holdExpiresAt && !isExpired && (
            <HoldCountdown
              expiresAt={booking.holdExpiresAt}
              onExpired={handleExpired}
            />
          )}

          {isExpired ? (
            <div className="payment-page__message" role="alert">
              <p>{t("payment.expired")}</p>
              <Link to={`/properties/${booking.propertyId}`}>
                {t("payment.backToProperty")}
              </Link>
            </div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: stripeAppearance,
                // The payment form lives in Stripe's iframe, so it loads the site font itself.
                fonts: [{ cssSrc: STRIPE_FONTS_CSS }],
                locale: "auto",
              }}
            >
              <PaymentForm
                submitLabel={
                  isRequest
                    ? t("payment.requestButton", { total })
                    : t("payment.payButton", { total })
                }
                returnUrl={`${window.location.origin}/trips/${booking.id}/confirmation`}
              />
            </Elements>
          )}

          <p className="payment-page__secure">{t("payment.secureNote")}</p>
        </section>

        <aside className="payment-page__summary">
          <div className="payment-page__property">
            {booking.propertyImageUrl ? (
              <img src={booking.propertyImageUrl} alt="" />
            ) : (
              <div className="payment-page__thumb">
                <PhotoPlaceholder />
              </div>
            )}
            <div>
              <strong>{booking.propertyTitle}</strong>
              <span>
                {[booking.propertyCity, booking.propertyCountry]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          </div>
          <dl className="payment-page__facts">
            <div>
              <dt>{t("checkout.dates")}</dt>
              <dd>
                {isoDate.formatIsoDate(booking.checkIn, i18n.language)} →{" "}
                {isoDate.formatIsoDate(booking.checkOut, i18n.language)}
              </dd>
            </div>
            <div>
              <dt>{t("checkout.guests")}</dt>
              <dd>
                {guestsUtils.describe(t, guestsUtils.ofBooking(booking))}
              </dd>
            </div>
            <div className="payment-page__total">
              <dt>{t("price.total")}</dt>
              <dd>{total}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </div>
  );
}

export default PaymentPage;